import { JMCCarousel } from "@components/JMCCarousel/JMCCarousel";
import { JMCHeaderHero } from "@components/JMCHeaderHero/JMCHeaderHero";
import { JMCHero } from "@components/JMCHero/JMCHero";
import ShowPrivacyPolicyUpdate from "@components/ShowPrivacyPolicyNotification/ShowPrivacyPolicyNotification";
import { ShowSSOBanner } from "@components/ShowSSOBanner/ShowSSOBanner";
import { ImagePosition } from "@jmc/solid-design-system/src/components/atoms/Hero/enum/ImagePosition.enum";
import { Hero } from "@jmc/solid-design-system/src/components/atoms/Hero/Hero";
import { Color, Variant } from "@jmc/solid-design-system/src/components/molecules/HeaderHero/HeaderHero";
import { HEADING_BACKGROUNDS } from "@jmc/solid-design-system/src/components/molecules/Heading/Heading";
import { Heading } from "@jmc/solid-design-system/src/components/molecules/Heading/Heading";
import relativeUnitToAbsolute from "@jmc/solid-design-system/src/utils/relativeUnitToAbsolute";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import {
    CMSCarouselProps,
    CMSEventProps,
    CMSFootnotes,
    CMSHeroProps,
    CMSProductProps,
    CMSSpecialtyProps,
} from "@types";
import { default as cmsMapper } from "@utils/cms-mapper";
import classnames from "classnames";
import { IGatsbyImageData } from "gatsby-plugin-image";
import React from "react";

import style from "./style.module.scss";

interface PropTypes {
    title: JSX.Element | string;
    renderBreadcrumb?: () => JSX.Element;
    headerVisual?: IGatsbyImageData | string;
    headerSize?: "Extra Small" | "Small" | "Large" | "Medium" | "auto";
    headerBackground?: HEADING_BACKGROUNDS | Color; // TODO: Rebranding: Remove HEADING_BACKGROUNDS after rebranding
    variant?: Variant;
    hero?: CMSHeroProps;
    carousel?: CMSCarouselProps;
    relatedTo?: CMSProductProps | CMSSpecialtyProps | CMSEventProps;
    imageFocus?: string;
    marginXl?: boolean;
    mobilePadding?: boolean;
    footnotes?: CMSFootnotes[];
    shouldAddProductTheme?: boolean;
    placement?: string;
    info?: JSX.Element; // TODO: Not in use, can be removed after rebranding
    firstSectionColor?: Color;
    bannerDisclaimer?: JSX.Element;
    hasNextSectionEqualColor?: boolean;
}

export const PageHeader: React.FunctionComponent<PropTypes> = ({
    title,
    renderBreadcrumb = null,
    headerVisual,
    headerSize,
    headerBackground = null,
    variant,
    hero = null,
    carousel = null,
    imageFocus = "center-center",
    marginXl = false,
    mobilePadding = false,
    footnotes,
    shouldAddProductTheme = true,
    placement,
    info = null,
    firstSectionColor = undefined,
    bannerDisclaimer,
    hasNextSectionEqualColor = false,
}: PropTypes) => {
    const breadcrumb = renderBreadcrumb && renderBreadcrumb();
    const { jnjFullBranded } = useJnjBranding();

    const mappedBackgroundColor = jnjFullBranded ? cmsMapper.mapHeaderHeroColor(headerBackground) : headerBackground;

    const getHeaderSize = (): string => {
        switch (headerSize) {
            case "auto":
                return relativeUnitToAbsolute("auto");
            case "Extra Small":
                return relativeUnitToAbsolute("15vh");
            case "Small":
                return relativeUnitToAbsolute("30vh");
            case "Medium":
                return relativeUnitToAbsolute("45vh");
            case "Large":
                return relativeUnitToAbsolute("70vh");
            default:
                return relativeUnitToAbsolute("auto");
        }
    };

    const height = getHeaderSize();
    const heroHeader = (): JSX.Element => {
        if (jnjFullBranded) {
            return (
                <JMCHeaderHero
                    hero={hero}
                    footnotes={footnotes}
                    placement={placement}
                    height={headerSize}
                    color={mappedBackgroundColor}
                    variant={variant}
                    sidesColor={firstSectionColor}
                />
            );
        } else {
            const heroProps = cmsMapper.mapHeroProps(hero);
            return (
                <>
                    {/*if hero title is empty it will take page title(hidden) in h1 tag for SEO */}
                    {heroProps.title === "" && title && <h1 style={{ display: "none" }}>{title}</h1>}
                    {/* This div has been added to stop the Hero Header from hiding the breadcrumb */}
                    <div>
                        <JMCHero
                            hero={hero}
                            height={height}
                            footnotes={footnotes}
                            placement={placement}
                            titleComponent={"h1"}
                            loading="eager"
                        />
                    </div>
                </>
            );
        }
    };

    const carouselHeader = (): JSX.Element => {
        const carouselProps = cmsMapper.mapCarouselProps(carousel);
        const firstSlide = carouselProps.slides[0];
        return (
            <>
                {/*if firstSlide title is empty it will take page title(hidden) in h1 tag for SEO */}
                {firstSlide && firstSlide.title === "" && title && <h1 style={{ display: "none" }}>{title}</h1>}
                <JMCCarousel
                    {...carouselProps}
                    placement={placement}
                    footnotes={footnotes}
                    isPageHeader
                    loading="eager"
                />
            </>
        );
    };

    if (!shouldAddProductTheme) {
        return null;
    }

    const isService = !firstSectionColor && !headerBackground;

    const carouselBackground =
        headerBackground && Object.values(Color).includes(headerBackground?.toLowerCase() as Color)
            ? (headerBackground?.toLowerCase() as Color)
            : Color.DEFAULT;

    return (
        <header
            data-test-id="main-header"
            className={
                jnjFullBranded &&
                carousel &&
                (carouselBackground
                    ? style[`backgroundColor__${carouselBackground}`]
                    : style[`backgroundColor__${Color.DEFAULT}`])
            }
        >
            {breadcrumb && jnjFullBranded && (
                <div className={classnames(style.breadcrumb_branded)} data-test-id="Heading.Breadcrumb" data-nosnippet>
                    {breadcrumb}
                </div>
            )}
            {!carousel && !hero && !headerVisual && !jnjFullBranded && (
                <Heading
                    title={title}
                    height={height}
                    backgroundColor={headerBackground}
                    marginXl={marginXl}
                    info={info}
                />
            )}
            {!carousel && !hero && !headerVisual && jnjFullBranded && (
                <JMCHeaderHero
                    title={title as JSX.Element}
                    height={headerSize}
                    color={mappedBackgroundColor}
                    variant={variant}
                    sidesColor={firstSectionColor}
                />
            )}
            {headerVisual && !jnjFullBranded && (
                <div className={headerSize === "Extra Small" ? style.heroContainer : undefined}>
                    {typeof headerVisual === "string" ? (
                        <Hero
                            id="PageHeader"
                            image={headerVisual}
                            height={height}
                            title={title}
                            imagePosition={imageFocus as ImagePosition}
                            marginXl={marginXl}
                            mobilePadding={mobilePadding}
                            fullWidth
                            contentAlignment="left"
                        />
                    ) : (
                        <JMCHero
                            id="PageHeader.FullBackground"
                            hero={{
                                background_image: { url: "", gatsbyImageData: headerVisual },
                                display_title: title,
                                display_options: {
                                    background_color: "transparent",
                                    full_width: true,
                                    align_content: "left",
                                    align_content_vertically: "center",
                                    image_focus: { image_focus: imageFocus as ImagePosition },
                                },
                            }}
                            height={height}
                            loading="eager"
                            titleComponent="h1"
                            titleVariant="h1" // see AFWN-6311
                            marginXl={marginXl}
                        />
                    )}
                </div>
            )}
            {headerVisual && jnjFullBranded && (
                <JMCHeaderHero
                    id={typeof headerVisual === "string" ? "PageHeader" : "PageHeader.FullBackground"}
                    height={headerSize}
                    hero={{
                        background_image: {
                            url: typeof headerVisual === "string" ? headerVisual : null,
                            gatsbyImageData: typeof headerVisual === "string" ? null : headerVisual,
                        },
                        display_title: title,
                        display_options: {
                            image_focus: { image_focus: imageFocus as ImagePosition },
                        },
                    }}
                    color={mappedBackgroundColor}
                    sidesColor={firstSectionColor}
                    variant={variant}
                />
            )}

            {hero && heroHeader()}
            {carousel && carouselHeader()}
            {breadcrumb && !jnjFullBranded && (
                <div className={classnames(style.breadcrumb)} data-test-id="Heading.Breadcrumb" data-nosnippet>
                    {breadcrumb}
                </div>
            )}
            {jnjFullBranded && (
                <div
                    className={classnames(
                        firstSectionColor ? style[`banner__${firstSectionColor}`] : style[`banner__${Color.DEFAULT}`],
                    )}
                >
                    <div
                        className={classnames(
                            style.banner,
                            (isService || hasNextSectionEqualColor) && style["padding-bottom"],
                            !isService && !hasNextSectionEqualColor && style["next-section-different-color"],
                        )}
                    >
                        <ShowSSOBanner />
                        <ShowPrivacyPolicyUpdate currentPage={placement} />
                        {bannerDisclaimer}
                    </div>
                </div>
            )}
        </header>
    );
};

export default PageHeader;
